import { useParams } from "react-router-dom";

import { FilterIcon, RefreshIcon, TrashIcon } from "@/assets/icons";
import {
  BRING_YOUR_OWN_DATA_DATA_SETTINGS_TOUR_ID,
  BRING_YOUR_OWN_DATA_DATASET_CONFIGURATION_TOUR_ID
} from "@/components/onboarding/configs/bring-your-own-data.mocks";
import ButtonMenu, { ButtonMenuItem } from "@/components/ui/buttonMenu";
import { COUNTRY_NAME } from "@/constants/dataset";
import { useDownloadDataset } from "@/hooks/dataset-managment/useDownloadDataset";
import {
  EModuleName,
  EModulePermission,
  useGetUserPermission
} from "@/hooks/permission/useGetUserPermission";
import { useGuardPermission } from "@/hooks/permission/useGuardPermission";
import { useAppFeatures } from "@/hooks/useAppFeatures";
import { CountryCode, SpatialAggregationType } from "@/types/common";
import {
  DatasetMetadata,
  EDatasetConnector,
  ExportDatasetFileType
} from "@/types/dataset";
import { EOutputAnalysisType } from "@/types/spatial";

import { Button } from "equator-design-system";
import { twMerge } from "tailwind-merge";

import AggregationBadge from "../AggregationBadge";

export interface FilterSectionProps {
  metadata: DatasetMetadata | undefined;
  isLoading: boolean;
  isDatasetPublished: boolean;
  handleAddMoreDataset: () => void;
  handleReplaceDataset: () => void;
  handleAddNewRowDataset: () => void;
  setshowDataSettingPopup: (show: boolean) => void;
  handleDeleteDataset: () => void;
  handleDeleteRows?: (id?: string) => void;
  deleteRowCount: number;
  resetDeleteRow: () => void;
  isSyncing?: boolean;
  handleSyncDataset?: () => void;
}

export default function DatasetConfigurationSection({
  metadata,
  isLoading,
  isDatasetPublished,
  handleDeleteDataset,
  handleAddMoreDataset,
  handleReplaceDataset,
  handleAddNewRowDataset,
  setshowDataSettingPopup,
  handleDeleteRows,
  deleteRowCount,
  resetDeleteRow,
  isSyncing = false,
  handleSyncDataset
}: Readonly<FilterSectionProps>) {
  const { id } = useParams();
  const {
    isRowDatasetManagementEnabled,
    isGeoJsonDownloadEnabled,
    isGranularRbacEnabled
  } = useAppFeatures();
  const guardPermission = useGuardPermission();
  const userPermissions = useGetUserPermission();
  const isViewDataGranted =
    userPermissions[EModuleName.DATASET_MANAGEMENT][EModulePermission.VIEW];
  const isEditDataGranted =
    userPermissions[EModuleName.DATASET_MANAGEMENT][EModulePermission.EDIT];
  const isCreateDataGranted =
    userPermissions[EModuleName.DATASET_MANAGEMENT][EModulePermission.CREATE];
  const isDeleteDataGranted =
    userPermissions[EModuleName.DATASET_MANAGEMENT][EModulePermission.DELETE];

  const isDataSettingsDisabled = isLoading || isDatasetPublished;
  // Sync replaces Edit Data for a connector-sourced dataset. Without
  // connector_info there is no record to pull, so it stays on Edit Data.
  const isTargetinSourced =
    metadata?.connector === EDatasetConnector.TARGETIN &&
    !!metadata.connector_info;

  // With granular RBAC, actions stay visible and blocked ones show a toast
  const isActionShown = (isGranted: boolean) =>
    isGranularRbacEnabled || isGranted;
  // Export needs only view with granular RBAC
  const isDownloadGranted = isGranularRbacEnabled
    ? isViewDataGranted
    : isEditDataGranted;

  const isShowEditDataButton = isActionShown(
    isEditDataGranted || isCreateDataGranted || isDeleteDataGranted
  );
  const hasDatasetConfigurationBadges =
    deleteRowCount === 0 &&
    (!!metadata?.spatial_aggregation_type || !!metadata?.country_id);

  const { handleDownloadDataset } = useDownloadDataset();
  const getEditDataMenu = (): ButtonMenuItem[] => {
    const output = [];
    // If Dataset have metadata spatial_aggregation_type, disable Add New Row feature until updated to support it
    if (isRowDatasetManagementEnabled && isActionShown(isCreateDataGranted)) {
      output.push(
        {
          name: "Row Action",
          id: "row-action-header",
          isHeader: true
        },
        {
          name: "Add New Row",
          id: "add-new-row",
          onClick: guardPermission(isCreateDataGranted, handleAddNewRowDataset)
        },
        {
          name: "Dataset Action",
          id: "dataset-action-header",
          isHeader: true
        }
      );
    }
    if (isActionShown(isCreateDataGranted)) {
      output.push({
        name: "Add More Data",
        id: "add-more-data",
        onClick: guardPermission(isCreateDataGranted, handleAddMoreDataset)
      });
    }
    if (isActionShown(isEditDataGranted)) {
      output.push({
        name: "Replace All Data",
        id: "replace-all-data",
        onClick: guardPermission(isEditDataGranted, handleReplaceDataset)
      });
    }
    if (isActionShown(isDeleteDataGranted)) {
      output.push({
        name: "Delete Dataset",
        id: "delete-dataset",
        onClick: guardPermission(isDeleteDataGranted, handleDeleteDataset)
      });
    }
    return output;
  };

  const getDownloadMenu = (): ButtonMenuItem[] => {
    return [
      {
        name: "CSV",
        onClick: () =>
          handleDownloadDataset(ExportDatasetFileType.CSV, id as string)
      },
      {
        name: "XLSX",
        onClick: () =>
          handleDownloadDataset(ExportDatasetFileType.XLSX, id as string)
      },
      ...(isGeoJsonDownloadEnabled
        ? [
            {
              name: "GeoJSON",
              onClick: () =>
                handleDownloadDataset(
                  ExportDatasetFileType.GEOJSON,
                  id as string
                )
            }
          ]
        : [])
    ];
  };

  return (
    <div className="flex gap-4">
      <div className="flex-center gap-2">
        {isActionShown(isEditDataGranted) && deleteRowCount === 0 && (
          <Button
            variant="secondary"
            disabled={isDataSettingsDisabled}
            className={twMerge(
              "btn-secondary px-4",
              metadata?.published && "hidden"
            )}
            data-tourid={BRING_YOUR_OWN_DATA_DATA_SETTINGS_TOUR_ID}
            onClick={guardPermission(isEditDataGranted, () =>
              setshowDataSettingPopup(true)
            )}
          >
            <FilterIcon
              className={twMerge(
                "fill-teal-500",
                isDataSettingsDisabled && "fill-gainsboro"
              )}
              width={16}
            />
            <span className="font-semibold text-sm">Data Settings</span>
          </Button>
        )}
        {hasDatasetConfigurationBadges && (
          <div
            className="flex-center gap-2"
            data-tourid={BRING_YOUR_OWN_DATA_DATASET_CONFIGURATION_TOUR_ID}
          >
            {!!metadata?.spatial_aggregation_type && (
              <AggregationBadge
                value={
                  metadata.spatial_aggregation_type as Exclude<
                    SpatialAggregationType,
                    EOutputAnalysisType
                  >
                }
              />
            )}
            {!!metadata?.country_id && (
              <div className="border border-gainsboro bg-whisper rounded-lg flex-center px-2 h-6">
                <span className="font-semibold">
                  {COUNTRY_NAME[metadata?.country_id as CountryCode]}
                </span>
              </div>
            )}
          </div>
        )}
        {deleteRowCount > 0 && (
          <p className="pl-4 pt-4 font-semibold text-txtHeading">
            {deleteRowCount} row(s) selected
          </p>
        )}
      </div>

      <div className="ml-auto flex flex-row items-center gap-2">
        {deleteRowCount > 0 && (
          <Button
            className="btn-secondary px-6"
            onClick={() => {
              resetDeleteRow();
            }}
          >
            <span className="font-semibold text-sm">Cancel</span>
          </Button>
        )}
        {isDownloadGranted && deleteRowCount === 0 && (
          <ButtonMenu
            variant="secondary"
            title="Download"
            btnContainerClassName="w-[200px] !min-w-[100px] btn-secondary"
            menuClassName="top-[42px] w-[120px] left-0 h-fit z-[99]"
            menu={getDownloadMenu()}
            disabled={isLoading}
          />
        )}
        {isShowEditDataButton &&
          deleteRowCount === 0 &&
          (isTargetinSourced ? (
            <Button
              data-testid="dataset-sync-button"
              className="btn-secondary px-6"
              disabled={isLoading || isSyncing}
              onClick={
                handleSyncDataset &&
                guardPermission(isEditDataGranted, handleSyncDataset)
              }
            >
              <RefreshIcon width={16} height={16} className="fill-current" />
              <span className="font-semibold text-sm">Sync</span>
            </Button>
          ) : (
            <ButtonMenu
              variant="secondary"
              title="Edit Data"
              btnContainerClassName="w-[200px] !min-w-[100px] btn-secondary"
              menuClassName="top-[42px] w-full right-0 h-fit z-[99]"
              menu={getEditDataMenu()}
              disabled={isLoading}
            />
          ))}
        {deleteRowCount > 0 && (
          <Button
            className="btn-secondary px-6"
            onClick={() => handleDeleteRows?.()}
          >
            <TrashIcon className="fill-primary-teal" />
            <span className="font-semibold text-sm">Delete rows</span>
          </Button>
        )}
      </div>
    </div>
  );
}
